// src/components/PetDetails/PetDetailsComponent.jsx
import React, { useMemo, useRef, useState } from 'react';
import { Button, Box, Typography } from '@mui/material';
import gsap from 'gsap';
import TextFieldComponent from '../TextField/TextFieldComponent';
import DropDownComponent from '../DropDown/DropDownComponent';

/* ---------------- helpers ---------------- */
const getByPath = (obj, path) =>
  String(path)
    .split('.')
    .reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

const setByPathImmutable = (root, fullPath, value) => {
  const parts = String(fullPath).split('.');
  const out = Array.isArray(root) ? [...(root || [])] : { ...(root || {}) };
  let cur = out;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    const v = cur[k];
    cur[k] = Array.isArray(v)
      ? [...v]
      : v && typeof v === 'object'
        ? { ...v }
        : Number.isInteger(+parts[i + 1])
          ? []
          : {};
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = value;
  return out;
};

// Spelled-out ordinals for 1..10; falls back to "n-th" after that
const ordinalWord = (n) => {
  const map = [
    'first',
    'second',
    'third',
    'fourth',
    'fifth',
    'sixth',
    'seventh',
    'eighth',
    'ninth',
    'tenth',
  ];
  return map[n - 1] || `${n}th`;
};

/* ---------------- component ---------------- */
export default function PetDetailsComponent(props) {
  const {
    values = {},
    onChange,
    onNext, // should be passed by QuestionEngineComponent (this.handleNext)
    isMobile,
    information,
    field,
    customTitle,
  } = props;

  const numberOfPets = Number(values?.numberOfPets || 1);
  const [currentPetIndex, setCurrentPetIndex] = useState(0);

  // validation registry (children register their validators here)
  const validatorsRef = useRef({});
  const [validMap, setValidMap] = useState({});

  // refs for GSAP
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  // normalize info + fields
  const info = useMemo(
    () => information ?? field?.information ?? {},
    [information, field]
  );

  const fields = useMemo(() => {
    const arr = Array.isArray(info.petQuestions) ? info.petQuestions : [];
    return arr
      .filter((item) => item && typeof item.path === 'string' && item.type)
      .map(({ path, ...cfg }) => ({ path, cfg }));
  }, [info]);

  // title with ordinal word substitution
  const baseTitle =
    customTitle ||
    info.customTitle ||
    `Let’s get to know the first pet you’d like to cover.`;
  const title = baseTitle.replace(/first/i, ordinalWord(currentPetIndex + 1));

  // bubble changes with the correct indexed dot-path
  const handleChange = (name, value, ...rest) => {
    const indexedPath = name.replace(/^pet\./, `pets.${currentPetIndex}.`);
    const rootKey = indexedPath.split('.')[0];
    const nextAll = setByPathImmutable(values, indexedPath, value);
    onChange?.(rootKey, nextAll[rootKey], ...rest);
  };

  /* ---------- validation wiring ---------- */
  const registerValidation = (name, fn) => {
    if (typeof fn === 'function') validatorsRef.current[name] = fn;
  };

  const setValidationState = (name, isValid) => {
    setValidMap((m) => ({ ...m, [name]: isValid }));
  };

  const runAllValidators = async () => {
    const fns = Object.values(validatorsRef.current);
    if (!fns.length) return true;
    const results = await Promise.all(
      fns.map((fn) => {
        try {
          return fn();
        } catch {
          return false;
        }
      })
    );
    return results.every(Boolean);
  };

  /* ---------- GSAP helpers (match engine feel) ---------- */
  const runStaggerIn = () => {
    const el = containerRef.current;
    if (!el) return;
    const elements = el.querySelectorAll('.stagger-in');
    if (!elements.length) return;
    gsap.set(elements, { opacity: 0, y: 30 });
    gsap.to(elements, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      ease: 'power2.out',
      stagger: 0.1,
      overwrite: true,
    });
  };

  const fadeOutCurrent = () => {
    const el = containerRef.current;
    if (!el) return Promise.resolve();
    const elements = el.querySelectorAll('.stagger-in');
    return new Promise((resolve) => {
      gsap.to(elements, {
        opacity: 0,
        y: 20,
        duration: 0.9,
        ease: 'power2.inOut',
        onComplete: resolve,
      });
    });
  };

  const bounceButton = async () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const tl = gsap.timeline({ defaults: { overwrite: 'auto' } });
    tl.to(btn, { scale: 0.85, duration: 0.15, ease: 'power3.inOut' }).to(
      btn,
      { scale: 1, duration: 0.5, ease: 'elastic.out(1.75, 0.4)' },
      '>'
    );
    await new Promise((r) => tl.eventCallback('onComplete', r));
  };

  /* ---------- confirm / next pet ---------- */
  const handleConfirm = async () => {
    // 1) validate all fields (children will show their helper errors)
    const allOk = await runAllValidators();
    if (!allOk) {
      // optional UX: scroll to first invalid field
      const firstBad = Object.entries(validMap).find(
        ([, ok]) => ok === false
      )?.[0];
      if (firstBad) {
        const el = containerRef.current?.querySelector(
          `[name="${CSS.escape(firstBad)}"]`
        );
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // 2) animate button + fade fields
    await bounceButton();
    await fadeOutCurrent();

    // 3) go to next pet or next question
    if (currentPetIndex < numberOfPets - 1) {
      setCurrentPetIndex((p) => p + 1);
      // allow React to paint next set, then animate them in
      requestAnimationFrame(() => setTimeout(runStaggerIn, 0));
    } else {
      onNext?.();
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{ width: '100%', maxWidth: '739px', mx: 'auto' }}
    >
      <Typography
        className="stagger-in"
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: isMobile ? '1.25rem' : '1.5rem',
          lineHeight: isMobile ? '1.5rem' : '1.75rem',
          letterSpacing: '-0.03em',
          textAlign: 'center',
          mb: isMobile ? '2rem' : '3.6rem',
          whiteSpace: 'pre-line',
          maxWidth: '448px',
        }}
      >
        {title?.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
          /^\*\*[^*]+\*\*$/.test(part) ? (
            <strong key={i} style={{ fontWeight: 700 }}>
              {part.slice(2, -2)}
            </strong>
          ) : (
            part
          )
        )}
      </Typography>

      {/* Fields */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? 2 : 2.25,
          rowGap: '1.5rem', // space for validation helper text
        }}
      >
        {fields.map(({ path, cfg }) => {
          const indexedPath = path.replace(
            /^pet\./,
            `pets.${currentPetIndex}.`
          );
          const type = String(cfg.type || '').toLowerCase();
          const value = getByPath(values, indexedPath) ?? '';

          const common = {
            key: indexedPath,
            name: indexedPath,
            question: cfg,
            value,
            values,
            onChange: handleChange,
            isMobile,
            setValidationState, // <- wire validation feedback
            registerValidation, // <- children register their validators
          };

          return (
            <Box
              key={indexedPath}
              className="stagger-in"
              sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
            >
              {type === 'dropdown' ? (
                <DropDownComponent {...common} />
              ) : (
                <TextFieldComponent {...common} />
              )}
            </Box>
          );
        })}
      </Box>

      {/* CTA */}
      <Box
        className="stagger-in"
        sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}
      >
        <Button
          ref={buttonRef}
          onClick={handleConfirm}
          variant="contained"
          sx={{
            backgroundColor: '#C7CA00',
            color: '#162F3B',
            borderRadius: '20px',
            px: 3,
            py: 1,
            fontWeight: 700,
            fontSize: '1rem',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#B0B300' },
            width: 'auto',
          }}
        >
          {currentPetIndex < numberOfPets - 1 ? 'Next Pet' : 'Confirm'}
        </Button>
      </Box>
    </Box>
  );
}
